import { Modal } from "antd";
import FooterModal from "components/Button/FooterModal";
import useContaRecorrenteDetalhe from "core/features/conta-recorrente/hooks/useContaRecorrenteDetalhe";
import useSaveContaRecorrente from "core/features/conta-recorrente/hooks/useSaveContaRecorrente";
import { OperacaoEnum } from "core/resources/enums/conta";
import {
  ContaRecorrenteForm,
  ContaRecorrenteRequestData,
} from "features/conta-recorrente/typings";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useLoading } from "ui/components/GlobalLoading";
import FormContaRecorrente from "components/Features/Financeiro/FormConta/FormContaRecorrente";
import { SelectBooleanoDefaultValues } from "ui/components/Input/SelectBooleano";
import { showSuccess } from "infra/helpers/alerta";

interface ModalFormContaRecorrenteProps {
  operacao: OperacaoEnum;
  isOpen: boolean;
  onClose: () => any;
  onSuccess: () => any;
  id?: string;
  showRegisterAnother?: boolean;
  defaultData?: Partial<ContaRecorrenteForm>;
  refetch?: () => any;
}

const defaultValues: Partial<ContaRecorrenteForm> = {
  composicoes: [{}],
};

function ModalFormContaRecorrente({
  operacao,
  isOpen,
  onClose,
  onSuccess,
  id,
  showRegisterAnother = true,
  defaultData,
  refetch,
}: ModalFormContaRecorrenteProps) {
  const { data, isLoading, isFetching } = useContaRecorrenteDetalhe(id);

  const [registerAnother, setRegisterAnother] = useState(false);

  const { mutate: saveContaRecorrente, isLoading: isLoadingSave } =
    useSaveContaRecorrente();

  const { control, watch, reset, setValue, handleSubmit, getValues } =
    useForm<ContaRecorrenteForm>({
      defaultValues: {
        ...defaultValues,
        operacao,
        calcularJurosAutomatico:
          operacao === OperacaoEnum.Receita ? true : undefined,
        ...defaultData,
      },
      mode: "all",
    });

  const form = watch();

  useEffect(() => {
    if (data) {
      reset({
        ...defaultValues,
        ...defaultData,
        ...data,
        baixaAutomatica: data.baixaAutomatica
          ? SelectBooleanoDefaultValues.True
          : SelectBooleanoDefaultValues.False,
        gerarContaAutomatico: data.gerarContaAutomatico
          ? SelectBooleanoDefaultValues.True
          : SelectBooleanoDefaultValues.False,
        gerarEmDiaUtil: data.gerarEmDiaUtil
          ? SelectBooleanoDefaultValues.True
          : SelectBooleanoDefaultValues.False,
        formasRecebimentoIds: data.formasRecebimento.map((forma) => forma.id),
      });
    }
  }, [data, defaultData, reset]);

  useEffect(() => {
    if (!isOpen) {
      reset({ ...defaultValues, operacao, ...defaultData });
    }
  }, [data, defaultData, isOpen, operacao, reset]);

  const onSubmit = async (data: ContaRecorrenteForm) => {
    const requestData: ContaRecorrenteRequestData = {
      ...data,
      gerarEmDiaUtil:
        data.gerarEmDiaUtil === SelectBooleanoDefaultValues.True ? true : false,
      baixaAutomatica:
        data.baixaAutomatica === SelectBooleanoDefaultValues.True
          ? true
          : false,
      gerarContaAutomatico:
        data.gerarContaAutomatico === SelectBooleanoDefaultValues.True
          ? true
          : false,
    };

    saveContaRecorrente(requestData, {
      onSuccess: () => {
        if (!registerAnother) {
          showSuccess().then(({ isConfirmed }) => {
            if (isConfirmed) {
              onSuccess();
            }
          });
        } else {
          showSuccess();
          reset({
            ...defaultValues,
            operacao,
            ...defaultData,
          });
          refetch?.();
        }
      },
    });
  };

  const title = useMemo(() => {
    let result = null;

    if (operacao === OperacaoEnum.Despesa) {
      if (id) {
        result = "Editar Despesa Recorrente";
      } else {
        result = "Nova Despesa Recorrente";
      }
    } else if (id) {
      result = "Editar Receita Recorrente";
    } else {
      result = "Nova Receita Recorrente";
    }

    return result;
  }, [id, operacao]);

  useLoading(isLoading || isFetching);

  return (
    <Modal
      centered
      visible={isOpen}
      footer={
        <FooterModal
          isDisabled={isFetching}
          onSubmit={() => {
            handleSubmit(onSubmit)();
          }}
          showRegisterAnother={showRegisterAnother}
          registerAnother={registerAnother}
          setRegisterAnother={setRegisterAnother}
          isLoadingSave={isLoadingSave!}
          onClose={onClose}
        />
      }
      title={title}
      destroyOnClose
      onCancel={onClose}
      maskClosable={false}
      closable={true}
      width={"min(850px, 80vw)"}
      bodyStyle={{
        maxHeight: "80vh",
        overflow: "auto",
        overflowX: "hidden",
        paddingBottom: 16,
      }}
    >
      <FormContaRecorrente
        control={control}
        setValue={setValue}
        form={form}
        operacao={operacao}
        getValues={getValues}
      />
    </Modal>
  );
}

export default ModalFormContaRecorrente;
